import {
  PageGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PdfFont,
  ETextAlign,
} from '../pdf';
import { GUtil } from '../gutil';
import { GLabel, LabelData } from '../glabel';
import { Random } from '@educraft/base';
import '../draw/primitives';
import '../draw/string';
import '../draw/helpers';
import '../draw/label';
import '../draw/cut';

export const LCD_TABLE_WIDTH = PdfUnit.FromMillimeters(122);
export const LCD_TABLE_HEIGHT = PdfUnit.FromMillimeters(193);

export const LCD_TOP_HEIGHT = PdfUnit.FromMillimeters(18);
export const LCD_MARGIN = PdfUnit.FromMillimeters(5);
export const LCD_TABLE_MARGIN = PdfUnit.FromMillimeters(3);
export const LCD_TOP_WIDTH = PdfUnit.FromMillimeters(60);
export const LCD_ROUND_RADIUS = PdfUnit.FromMillimeters(8);

export const LCD_COLOR_PALETTES: string[][] = [
  ['#e6da73', '#e6cfe6'],
  ['#4ed88a', '#93edde'],
  ['#95a598', '#737f75'],
  ['#424db7', '#23b21e'],
  ['#45339b', '#8e008e'],
];

export interface LcdTableData {
  page: PageGen;
  rect1: PdfRect;
  rect2: PdfRect;
}

export function drawLcdTableRect(page: PageGen, rect: PdfRect, brush: PdfBrush): void {
  const inner = new PdfRect(
    rect.x + LCD_TABLE_MARGIN,
    rect.y + LCD_TABLE_MARGIN,
    rect.width - 2 * LCD_TABLE_MARGIN,
    rect.height - 2 * LCD_TABLE_MARGIN
  );
  const top = new PdfRect(
    rect.x + (rect.width - LCD_TOP_WIDTH) / 2,
    rect.y,
    LCD_TOP_WIDTH,
    LCD_TABLE_MARGIN * 2
  );

  const topRadius = Math.min(LCD_TOP_WIDTH, LCD_TABLE_MARGIN * 2) / 2;

  page.drawRoundedRectangle(null, brush, rect, LCD_ROUND_RADIUS);
  page.drawRoundedRectangle(null, PdfBrush.White, inner, LCD_ROUND_RADIUS);
  page.drawRoundedRectangle(null, brush, top, topRadius);
}

export function drawLcdTable(
  page: PageGen,
  random: Random,
  callback: (data: LcdTableData) => void
): void {
  const size = GUtil.lcdTableSize;

  const origin = new PdfPoint(
    (PdfSize.A4H.width - 2 * size.width) / 2,
    (PdfSize.A4.width - size.height) / 2
  );

  const brushes = GUtil.randomBrushes(random, LCD_COLOR_PALETTES);

  const table1 = new PdfRect(origin.x, origin.y, size.width, size.height);
  const table2 = new PdfRect(origin.x + size.width, origin.y, size.width, size.height);

  const rect1 = new PdfRect(
    table1.x + LCD_MARGIN,
    table1.y + LCD_TOP_HEIGHT,
    table1.width - 2 * LCD_MARGIN,
    table1.height - LCD_TOP_HEIGHT - LCD_MARGIN
  );
  const rect2 = new PdfRect(
    table2.x + LCD_MARGIN,
    table2.y + LCD_TOP_HEIGHT,
    table2.width - 2 * LCD_MARGIN,
    table2.height - LCD_TOP_HEIGHT - LCD_MARGIN
  );

  drawLcdTableRect(page, table1, brushes[0]);
  drawLcdTableRect(page, table2, brushes[1]);

  page.cutCross(origin);
  page.cutCross(new PdfPoint(origin.x + size.width * 2, origin.y + size.height));
  page.cutCross(new PdfPoint(PdfSize.A4H.width / 2, 0), true, false);

  callback({ page, rect1, rect2 });
}

export function drawLcdTableLabels(
  page: PageGen,
  labels: GLabel[],
  callback: (data: LcdTableData) => void
): void {
  drawLcdTable(page, { next: () => 0 } as Random, (data) => {
    const font = GUtil.baseFont;

    for (let i = 0; i < labels.length; i++) {
      const r1 = new PdfRect(
        data.rect1.x,
        data.rect1.y + (i * data.rect1.height) / labels.length,
        data.rect1.width,
        data.rect1.height / labels.length
      );
      const r2 = new PdfRect(
        data.rect2.x,
        data.rect2.y + (i * data.rect2.height) / labels.length,
        data.rect2.width,
        data.rect2.height / labels.length
      );

      const labelData1: LabelData = { rect: r1 };
      const labelData2: LabelData = { rect: r2 };

      page.drawLabel(labels[i], labelData1);
      page.drawLabel(labels[i], labelData2);

      page.drawRectangle(GUtil.basePen, null, r1);
      page.drawRectangle(GUtil.basePen, null, r2);
    }

    callback(data);
  });
}
